import { useCallback, useEffect, useState } from 'react';
import { fetchDetails } from '../services/api';

// Details fetched from lab websites during this session, by result id.
const cache = new Map();

/**
 * Details for a result: inline details when the search returned them,
 * otherwise fetched on demand from the lab's page (for sources that
 * support it). Returns { details, loading, error, retry }.
 */
export const useResultDetails = (model) => {
  const [state, setState] = useState({ details: null, loading: false, error: '' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!model) {
      setState({ details: null, loading: false, error: '' });
      return undefined;
    }
    if (model.hasInlineDetails) {
      setState({ details: model.details, loading: false, error: '' });
      return undefined;
    }
    if (cache.has(model.id)) {
      setState({ details: cache.get(model.id), loading: false, error: '' });
      return undefined;
    }
    if (!model.canFetchDetails) {
      setState({ details: {}, loading: false, error: '' });
      return undefined;
    }

    const controller = new AbortController();
    setState({ details: null, loading: true, error: '' });
    fetchDetails(model.link, model.sourceName, controller.signal)
      .then((details) => {
        cache.set(model.id, details);
        setState({ details, loading: false, error: '' });
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setState({ details: null, loading: false, error: err.message || 'Could not load details.' });
      });
    return () => controller.abort();
  }, [model, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
};
